import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AuthResponseDto } from "@/client/types.gen";

/** The signed-in user, derived from the generated response shape. */
type SessionUserDto = AuthResponseDto["user"];
import type { Role } from "@nl/shared";

import { AbilityProvider } from "./ability";
import { setViewAsTenantId } from "./api-client";
import { authControllerLogout, tenantsControllerViewAs } from "@/client/sdk.gen";
import { queryKeys, useMe } from "./queries";

/**
 * Signed-in user and tenant context.
 *
 * The role comes from the server-side session and cannot be switched in the
 * client. What remains is the platform role deliberately switching into a
 * tenant, which is checked and logged server-side.
 */

type SessionContextValue = {
  user: SessionUserDto | null;
  isLoading: boolean;
  role: Role | null;
  /** The tenant currently being worked in. */
  tenantId: string | null;
  /** Platform role only: the tenant currently being viewed. */
  viewAsTenantId: string | null;
  setViewAsTenant: (id: string | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const SessionContext = React.createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const client = useQueryClient();
  const { data, isLoading } = useMe();
  const [viewAs, setViewAs] = React.useState<string | null>(null);

  const user = data?.user ?? null;

  const setViewAsTenant = React.useCallback(
    (id: string | null) => {
      // The switch itself is a header on every request; this call is what
      // puts it in the audit log.
      if (id) void tenantsControllerViewAs({ path: { id } }).catch(() => undefined);
      setViewAsTenantId(id);
      setViewAs(id);
      // Refetch everything that depends on the tenant.
      void client.invalidateQueries();
    },
    [client],
  );

  const value = React.useMemo<SessionContextValue>(
    () => ({
      user,
      isLoading,
      role: user?.role ?? null,
      tenantId:
        user?.role === "platform"
          ? (viewAs ?? user.tenantId ?? null)
          : (user?.tenantId ?? null),
      viewAsTenantId: viewAs,
      setViewAsTenant,
      refresh: async () => {
        await client.invalidateQueries({ queryKey: queryKeys.me() });
      },
      logout: async () => {
        await authControllerLogout();
        setViewAsTenantId(null);
        client.clear();
        window.location.href = "/login";
      },
    }),
    [user, isLoading, viewAs, setViewAsTenant, client],
  );

  return (
    <SessionContext.Provider value={value}>
      <AbilityProvider userId={user?.id} viewAsTenantId={viewAs}>
        {children}
      </AbilityProvider>
    </SessionContext.Provider>
  );
}

export function useSessionContext(): SessionContextValue {
  const context = React.useContext(SessionContext);
  if (!context) {
    throw new Error("useSessionContext muss innerhalb von SessionProvider stehen");
  }
  return context;
}

/** Throws when no user is signed in. For pages behind the login. */
export function useCurrentUser(): SessionUserDto {
  const { user } = useSessionContext();
  if (!user) throw new Error("Kein angemeldeter Nutzer");
  return user;
}
